import React, { useState } from 'react';
import { Button } from '@tv2/ui-system/components/Button';
import { useUpdateModifications } from '../ModificationsProvider';
import { FooterType, useFooter } from '../../api/footer/useFooter';
import { Input } from './Input';
import Toast from '../Toast/Toast';

export function FooterAdminPanel({ footerData }: { footerData: FooterType }) {
  const [footer, setFooter] = useState<FooterType>(footerData);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>(0);
  const [selectedCategoryItemIndex, setSelectedCategoryItemIndex] = useState<number>(0);
  const [categoryInput, setCategoryInput] = useState<string>('');
  const [categoryItemInput, setCategoryItemInput] = useState<{ title: string; url: string }>({
    title: '',
    url: '',
  });
  const { saveFooter } = useFooter();

  const selectedCategory = footer.categories[selectedCategoryIndex];
  const selectedCategoryItem = selectedCategory.items[selectedCategoryItemIndex];

  const updateArea = useUpdateModifications();

  function addCategory() {
    if (!footer) return;
    const newCategory = {
      title: categoryInput,
      items: [],
    };

    footer.categories.push(newCategory);

    setFooter((prevFooter: FooterType) => ({
      ...prevFooter,
      categories: footer.categories,
    }));

    setCategoryInput('');
  }

  function addCategoryItem() {
    if (!footer) return;

    footer.categories[selectedCategoryIndex].items.push(categoryItemInput);

    setFooter((prevFooter: FooterType) => ({
      ...prevFooter,
      categories: footer.categories,
    }));
    setCategoryItemInput({ title: '', url: '' });
  }

  function removeCategory() {
    if (!footer) return;

    footer.categories.splice(selectedCategoryIndex, 1);

    setFooter((prevFooter: FooterType) => ({
      ...prevFooter,
      categories: footer.categories,
    }));
    setSelectedCategoryIndex(selectedCategoryIndex - 1 > -1 ? selectedCategoryIndex - 1 : 0);
    setSelectedCategoryItemIndex(0);
  }

  function removeCategoryItem() {
    if (!footer) return;

    footer.categories[selectedCategoryIndex].items.splice(selectedCategoryItemIndex, 1);

    setFooter((prevFooter: FooterType) => ({
      ...prevFooter,
      categories: footer.categories,
    }));
    setSelectedCategoryItemIndex(
      selectedCategoryItemIndex - 1 > -1 ? selectedCategoryItemIndex - 1 : 0
    );
  }

  async function updateFooter() {
    const category = {
      title: selectedCategory.title,
      items: selectedCategory.items,
    };
    const categoryItem = {
      title: selectedCategoryItem.title,
      url: selectedCategoryItem.url,
    };

    if (!footer) return;

    footer.categories[selectedCategoryIndex] = category;
    footer.categories[selectedCategoryIndex].items[selectedCategoryItemIndex] = categoryItem;

    saveFooter(footer).then(({ data, error }) => {
      if (data) {
        setFooter((prevFooter: FooterType) => ({
          ...prevFooter,
          categories: footer.categories,
        }));
        updateArea({ footer: { ...footer, categories: footer.categories } });
      }

      if (error) {
        setErrorMessage('Kunne ikke gemme footer. Prøv igen senere.');
      }
    });
  }

  return (
    <>
      {errorMessage && (
        <div className="my-4">
          <Toast message={errorMessage} onClose={() => setErrorMessage('')} />
        </div>
      )}
      <p className="typo-heading-l">Footer</p>
      <div>
        <form id="footer-form" onSubmit={updateFooter}>
          {footer.categories.length > 0 && (
            <div className="flex flex-col mt-2">
              <h3>
                <strong>Kategorier:</strong>
              </h3>
              <select
                id="categories"
                value={selectedCategoryIndex}
                onChange={e => {
                  setSelectedCategoryIndex(Number(e.target.value));
                  setSelectedCategoryItemIndex(0);
                }}
                className="border border-white p-1 font-bold"
              >
                {footer.categories.map((item, index) => (
                  <option key={index} value={index}>
                    {item.title}
                  </option>
                ))}
              </select>
              Titel:
              <div className="flex justify-between gap-1">
                <Input
                  value={selectedCategory.title}
                  name="category-title"
                  placeholder="Kategori titel"
                  onChange={event =>
                    setFooter(prevFooter => ({
                      ...prevFooter,
                      categories: [
                        ...prevFooter.categories.slice(0, selectedCategoryIndex),
                        { ...selectedCategory, title: event.target.value },
                        ...prevFooter.categories.slice(selectedCategoryIndex + 1),
                      ],
                    }))
                  }
                />
                <Button onClick={removeCategory} color="primary-red" size="small">
                  Fjern
                </Button>
              </div>
            </div>
          )}
          <div className="mt-2">
            Ny kategori:
            <div className="flex justify-between gap-1">
              <Input
                value={categoryInput}
                placeholder="Ny kategori titel"
                onChange={event => setCategoryInput(event.target.value)}
              />
              <Button onClick={addCategory} color="primary-green" size="small">
                Tilføj
              </Button>
            </div>
          </div>
          {selectedCategory.items.length > 0 && (
            <div className="flex flex-col">
              <h3>
                <strong>Menupunkter:</strong>
              </h3>
              <select
                id="category-items"
                value={selectedCategoryItemIndex}
                onChange={e => setSelectedCategoryItemIndex(Number(e.target.value))}
                className="border border-white p-1 font-bold"
              >
                {selectedCategory.items.map((item, index) => (
                  <option key={index} value={index}>
                    {item.title}
                  </option>
                ))}
              </select>
              <label>
                Titel:
                <div className="flex justify-between gap-1">
                  <Input
                    value={selectedCategoryItem.title}
                    name="category-item-title"
                    onChange={event =>
                      setFooter(prevFooter => ({
                        ...prevFooter,
                        categories: [
                          ...prevFooter.categories.slice(0, selectedCategoryIndex),
                          {
                            ...selectedCategory,
                            items: [
                              ...selectedCategory.items.slice(0, selectedCategoryItemIndex),
                              { ...selectedCategoryItem, title: event.target.value },
                              ...selectedCategory.items.slice(selectedCategoryItemIndex + 1),
                            ],
                          },
                          ...prevFooter.categories.slice(selectedCategoryIndex + 1),
                        ],
                      }))
                    }
                  />
                  <Button onClick={removeCategoryItem} color="primary-red" size="small">
                    Fjern
                  </Button>
                </div>
              </label>
              <label>
                URL:
                <Input
                  type="url"
                  value={selectedCategoryItem.url}
                  name="category-item-url"
                  onChange={event =>
                    setFooter(prevFooter => ({
                      ...prevFooter,
                      categories: [
                        ...prevFooter.categories.slice(0, selectedCategoryIndex),
                        {
                          ...selectedCategory,
                          items: [
                            ...selectedCategory.items.slice(0, selectedCategoryItemIndex),
                            { ...selectedCategoryItem, url: event.target.value },
                            ...selectedCategory.items.slice(selectedCategoryItemIndex + 1),
                          ],
                        },
                        ...prevFooter.categories.slice(selectedCategoryIndex + 1),
                      ],
                    }))
                  }
                />
              </label>
            </div>
          )}
          <div className="mt-2">
            Nyt menupunkt:
            <div className="flex justify-between gap-1">
              <Input
                value={categoryItemInput.title}
                placeholder="Menupunkt titel"
                onChange={event =>
                  setCategoryItemInput({ title: event.target.value, url: categoryItemInput.url })
                }
              />
              <Input
                value={categoryItemInput.url}
                placeholder="Menupunkt URL"
                onChange={event =>
                  setCategoryItemInput({ title: categoryItemInput.title, url: event.target.value })
                }
              />

              <Button color="primary-green" darkMode size="small" onClick={addCategoryItem}>
                Tilføj
              </Button>
            </div>
          </div>
          <div className="mt-4">
            <Button color="primary" darkMode size="medium" onClick={updateFooter}>
              Gem
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
